import React, { useState, useEffect, useContext } from "react"
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';

import LanguageContext from "../contexts/LanguageContext"

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import APIS from "../utils/APIS"
import queryString from "query-string"


const ResetPassword = () => {
  const navigate = useNavigate();

  const { actLanguage, languageValues } = useContext(LanguageContext)

  const passwordValidation = {
    required: languageValues?.passwordValidation?.required[actLanguage],
    regex: /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*,-.:\;<>?@[\]^_`{|}~])/,
    matches: languageValues?.passwordValidation?.match1[actLanguage],
    majMinRegex: /(?=.*[a-z])(?=.*[A-Z])/,
    majMinMatches: languageValues?.passwordValidation?.match2[actLanguage],
    digitSpecialCharRegex: /(?=.*\d)(?=.*[ !"#$%&'()*,-.:\;<>?@[\]^_`{|}~])/,
    digitSpecialCharMatches: languageValues?.passwordValidation?.match3[actLanguage],
    min: languageValues?.passwordValidation?.min[actLanguage],
  }


  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const [isValidRequest, setIsValidRequest] = useState(null)
  const [infos, setInfos] = useState({})
  useEffect(() => {
    const urlParams = queryString.parse(location.search)
    window.history.replaceState(null, null, window.location.pathname)
    if (urlParams.id && urlParams.uuid) {
      APIS.checkResetPsswd(urlParams).then((result) => {
        if (result.message == "ok") {
          setInfos({ id: urlParams.id, uuid: urlParams.uuid })
          setIsValidRequest(true)
        } else {
          setIsValidRequest(false)
        }
      })
    } else {
      setIsValidRequest(false)
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{languageValues?.resetPasswordPage?.title[actLanguage] || ''} | TheLab</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >

        {isValidRequest === "final" &&
          <Container maxWidth="sm">
            <Box sx={{ mb: 2, textAlign:"center" }}>
              <Typography
                color="text.blackTitle"
                variant="h2"
              >
                {languageValues?.resetPasswordPage?.title[actLanguage]}
              </Typography>
            </Box>

            <Box sx={{
              marginTop:"30px",
              marginBottom:"15px",
              fontSize:"20px",
              textAlign:"center",
              color : "text.blackTitle",
            }}>
              {languageValues?.resetPasswordPage?.afterText[actLanguage]}
            </Box>
            <Box sx={{ py: 2 }}>
              <RouterLink to="/login">
                <Button
                  color="primary"
                  fullWidth
                  size="large"
                  variant="contained"
                >
                  {languageValues?.loginPage?.button[actLanguage]}
                </Button>
              </RouterLink>
            </Box>
          </Container>
        }

        {isValidRequest === true &&
          <Container maxWidth="sm">
            <Formik
              initialValues={{
                password: '',
                confirm : '',
              }}
              validationSchema={Yup.object().shape({
                password: Yup.string()
                  .min(8, passwordValidation.min)
                  .matches(passwordValidation.regex, passwordValidation.matches)
                  .required(passwordValidation.required),
                confirm: Yup.string()
                  .oneOf([Yup.ref('password'), null], languageValues?.passwordValidation?.confirmSame[actLanguage])
                  .required(passwordValidation.required),
              })}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                APIS.setPsswd({
                  id: infos.id,
                  uuid: infos.uuid,
                  password: values.password,
                  confirm : values.confirm,
                }).then(result => {
                  if (result?.message == "ok") {
                    resetForm({})
                    setIsValidRequest("final")
                  } else {
                    resetForm({})
                    setSubmitting(false);
                    alert("Une erreur est survenue")
                  }
                })
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                setFieldValue,
                handleSubmit,
                isSubmitting,
                touched,
                values
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3, textAlign:"center" }}>
                    <Typography
                      color="textBlack"
                      variant="h2"
                    >
                      {languageValues?.resetPasswordPage?.title[actLanguage]}
                    </Typography>
                  </Box>
                  <TextField
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    margin="normal"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    variant="outlined"
                    placeholder={languageValues?.loginPage?.passwordField[actLanguage]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{cursor:"pointer"}} onClick={() => { setShowPassword(!showPassword) }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    error={Boolean(touched.confirm && errors.confirm)}
                    fullWidth
                    helperText={touched.confirm && errors.confirm}
                    margin="normal"
                    name="confirm"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"}
                    value={values.confirm}
                    variant="outlined"
                    placeholder={"Confirmation"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{cursor:"pointer"}} onClick={() => { setShowPassword(!showPassword) }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ py: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {languageValues?.resetPasswordPage?.button[actLanguage]}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Container>
        }

        {isValidRequest === false &&
          <Container maxWidth="sm">
            <Box sx={{textAlign:"center", fontSize:"30px"}}>
              {languageValues?.notifError[actLanguage]}
            </Box>
          </Container>
        }

      </Box>
    </>
  );
};

export default ResetPassword;
